import React, { useEffect, useState } from "react";
import welcomeBack from "assets/images/welcome-back.png";
import { useNavigate } from "react-router-dom";
import { useLogin } from "utils/api/auth.api";
import { type UserLogin } from "utils/types/user.type";
import { type SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Input } from "components/input";
import ForgotPassword from "components/forgotPassword";

export default function Login() {
  const navigate = useNavigate();
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  const {
    mutate: userLogin,
    isSuccess,
    data: response,
    isLoading,
    isError,
    error,
  } = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserLogin>();

  const onSubmit: SubmitHandler<UserLogin> = (data) => userLogin(data);

  useEffect(() => {
    if (isSuccess && response) {
      const { accessToken, refreshToken } = response;
      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      toast.success("Login successfully!");
      navigate("/transaction-history");
    }
    if (isError) {
      toast.error(error?.message || "An error occurred during login.");
    }
  }, [isSuccess, isError, error, response, navigate]);
  return (
    <section className="login-section">
      <div className="grid lg:grid-cols-12 h-screen gap-3">
        <div className="lg:col-span-6 relative lg:overflow-hidden">
          <div className="login-bg h-full flex items-center justify-center">
            <img
              className="max-w-xs lg:max-w-none object-cover object-right-top"
              src={welcomeBack}
              alt="title"
            />
          </div>
        </div>
        <div className="lg:col-span-6 flex items-center justify-center">
          <div className="login-wrapper w-full max-w-md mx-auto p-6">
            <h5 className="text-center text-black-800 text-3xl font-bold mb-8 xl:mb-16">
              Welcome back
            </h5>
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises  */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <Input
                  isInvalid={Boolean(errors?.email)}
                  errorMessage={errors?.email?.message}
                  label="Email"
                  type="email"
                  name="email"
                  register={register}
                  rules={{
                    required: "Email is required",
                  }}
                />
              </div>

              <div className="mb-4">
                <Input
                  isInvalid={Boolean(errors?.password)}
                  errorMessage={errors?.password?.message}
                  label="Password"
                  type="password"
                  name="password"
                  register={register}
                  rules={{
                    required: "Password is required",
                  }}
                />
              </div>

              <div className="mb-4 text-center">
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordOpen(true)}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium underline"
                >
                  Forgot Password?
                </button>
              </div>

              <div className="lg:mx-8 mb-8">
                <button
                  type="submit"
                  className="bg-cyan-800 hover:bg-cyan-300 text-black-800 text-xl font-medium border border-cyan-800 rounded-lg w-full p-3.5 transition"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div
                      className="w-8 h-8 rounded-full animate-spin mx-auto
                      border-4 border-solid border-cyan-800 border-t-transparent"
                    ></div>
                  ) : (
                    "Login to your account"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ForgotPassword isOpen={isForgotPasswordOpen} setIsOpen={setIsForgotPasswordOpen} />
    </section>
  );
}
